// Read/write ~/.avatar/config.json (OAuth credentials) and ~/.avatar/state.json
// (non-credential user state). Validates with Zod, enforces chmod 600 on config.
import { homedir } from "node:os";
import { join } from "node:path";
import {
  type UserConfig,
  type UserState,
  userConfigSchema,
  userStateSchema,
} from "../types/config-schema.js";
import { ensureDir, pathExists, readJson, writeJsonAtomic } from "./filesystem-helpers.js";

export const AVATAR_HOME = join(homedir(), ".avatar");
export const USER_CONFIG_PATH = join(AVATAR_HOME, "config.json");
export const USER_STATE_PATH = join(AVATAR_HOME, "state.json");
export const AUDIT_LOG_PATH = join(AVATAR_HOME, "audit.log");
export const BACKUPS_DIR = join(AVATAR_HOME, "backups");

// chmod 600 — owner-only read/write. Matches spec for credential files.
const SECRET_FILE_MODE = 0o600;

export async function ensureAvatarHome(): Promise<void> {
  await ensureDir(AVATAR_HOME);
}

export async function readUserConfig(): Promise<UserConfig | null> {
  if (!(await pathExists(USER_CONFIG_PATH))) return null;
  const raw = await readJson<unknown>(USER_CONFIG_PATH);
  const parsed = userConfigSchema.safeParse(raw);
  if (!parsed.success) return null;
  return parsed.data;
}

export async function writeUserConfig(config: UserConfig): Promise<void> {
  await ensureAvatarHome();
  await writeJsonAtomic(USER_CONFIG_PATH, config, SECRET_FILE_MODE);
}

export async function clearUserConfig(): Promise<void> {
  if (await pathExists(USER_CONFIG_PATH)) {
    const { promises: fs } = await import("node:fs");
    await fs.unlink(USER_CONFIG_PATH);
  }
}

export async function readUserState(): Promise<UserState> {
  if (!(await pathExists(USER_STATE_PATH))) {
    return userStateSchema.parse({});
  }
  const raw = await readJson<unknown>(USER_STATE_PATH);
  const parsed = userStateSchema.safeParse(raw);
  if (!parsed.success) return userStateSchema.parse({});
  return parsed.data;
}

export async function writeUserState(state: UserState): Promise<void> {
  await ensureAvatarHome();
  await writeJsonAtomic(USER_STATE_PATH, state);
}

// Token is considered expired if it would expire in the next 60 seconds.
// 60s buffer accounts for slow network round-trip on the upcoming request.
export function isTokenExpired(config: UserConfig): boolean {
  const expiresAt = Date.parse(config.expires_at);
  return Number.isNaN(expiresAt) || expiresAt - Date.now() < 60_000;
}

// Lỗi báo caller cần chạy `avatar login` lại (chưa login HOẶC refresh fail).
export class NoValidTokenError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "NoValidTokenError";
  }
}

// Decode JWT exp của id_token (giây). Trả null nếu không parse được.
// id_token có lifetime RIÊNG, có thể hết hạn dù access_token (expires_at) còn —
// nên phải check exp của chính id_token, không chỉ dựa expires_at (S2).
async function idTokenIsExpired(idToken: string): Promise<boolean> {
  const { decodeIdToken } = await import("./google-oauth-device-flow.js");
  try {
    const claims = decodeIdToken(idToken);
    const nowSec = Math.floor(Date.now() / 1000);
    // 60s buffer cho network round-trip của request sắp gọi.
    return claims.exp - 60 < nowSec;
  } catch {
    return true; // không decode được = coi như không hợp lệ.
  }
}

// v3: Lấy id_token CÒN HẠN + ĐÃ VERIFY để gọi Supabase get-pack. Tự refresh nếu hết hạn.
//
// SECURITY (S1+S2): MỌI nhánh đều decode + verifyIdTokenClaims id_token cuối cùng
// trước khi return (issuer/audience/email_verified/exp). Không nhánh nào trả token
// chưa-verify hoặc đã-hết-hạn xuống downloader.
//
// ⚠️ #187: verify KHÔNG còn kiểm `hd`. Account ngoài NAL lấy được token hợp lệ ở
// đây — đúng chủ đích. Biên giới NAL do SERVER get-pack cưỡng chế (401), không
// phải bước này; đừng đọc hàm này như cổng chặn workspace.
//
//   - chưa login → NoValidTokenError
//   - id_token còn hạn (theo JWT exp) → verify → trả
//   - hết hạn → refresh → nếu Google KHÔNG trả id_token mới (hợp lệ theo OAuth khi
//     thiếu scope) thì id_token cũ vẫn hết hạn → throw, KHÔNG fallback im lặng
//   - refresh fail (refresh_token revoked) → NoValidTokenError (prompt login)
export async function getValidIdToken(): Promise<string> {
  const config = await readUserConfig();
  if (!config) {
    throw new NoValidTokenError("Chưa đăng nhập. Chạy 'avatar login' trước.");
  }

  const { refreshAccessToken, decodeIdToken, verifyIdTokenClaims } = await import(
    "./google-oauth-device-flow.js"
  );

  // Fast path: id_token hiện tại còn hạn (theo chính JWT exp của nó, không phải expires_at).
  if (!(await idTokenIsExpired(config.id_token))) {
    verifyIdTokenClaims(decodeIdToken(config.id_token));
    return config.id_token;
  }

  // id_token hết hạn → refresh.
  let refreshed: { access_token: string; expires_in: number; id_token?: string };
  try {
    refreshed = await refreshAccessToken(config.refresh_token);
  } catch (err) {
    throw new NoValidTokenError(
      `Token hết hạn và refresh thất bại (${err instanceof Error ? err.message : err}). Chạy 'avatar login' lại.`,
    );
  }

  // S1: Google có thể KHÔNG trả id_token mới (OAuth spec, khi request thiếu scope).
  // KHÔNG fallback id_token cũ (đã hết hạn) — đó là lỗ hổng. Yêu cầu id_token mới.
  if (!refreshed.id_token) {
    throw new NoValidTokenError(
      "Refresh không trả id_token mới (id_token cũ đã hết hạn). Chạy 'avatar login' lại.",
    );
  }

  // S1: verify claims id_token MỚI trước khi tin (defense-in-depth, gồm exp).
  verifyIdTokenClaims(decodeIdToken(refreshed.id_token));

  const updated: UserConfig = {
    ...config,
    access_token: refreshed.access_token,
    id_token: refreshed.id_token,
    expires_at: new Date(Date.now() + refreshed.expires_in * 1000).toISOString(),
  };
  await writeUserConfig(updated);
  return refreshed.id_token;
}
